import { AuthContext, DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE } from "./auth-fe2.esm.js";
import { getDomain } from "./auth-fe3.esm.js";
import { useGraphqlQuery } from "./auth-fe4.esm.js";
import { parseAuthentication } from "./auth-fe5.esm.js";
import "./auth-fe6.esm.js";
import UserSession_default from "./auth-fe7.esm.js";
import ValidateResetPasswordToken_default from "./auth-fe8.esm.js";
import React, { useCallback, useMemo } from "react";
//#region src/AuthContextProvider.tsx
var normalizeAuthBaseUrl = (authBaseUrl) => authBaseUrl.replace(/\/+$/, "");
var getAuthBaseUrl = (authBaseUrl, env) => {
	if (authBaseUrl) return normalizeAuthBaseUrl(authBaseUrl);
	if (env) return getDomain(env);
	throw new Error("DEVELOPER ERROR: AuthContextProvider requires authBaseUrl or env");
};
/** TODO: Add documentation */
var AuthContextProvider = ({ trackError = () => {}, client, fetch: fetchFn = fetch, authBaseUrl, env, children }) => {
	const resolvedAuthBaseUrl = getAuthBaseUrl(authBaseUrl, env);
	const queryResult = useGraphqlQuery(client, UserSession_default, {});
	const logout = useCallback(() => {
		return fetchFn(`${resolvedAuthBaseUrl}/session/sign-out`, {
			method: "GET",
			credentials: "include"
		}).then(() => {
			window.location.reload();
		}).catch((e) => {
			trackError("logout", e);
			window.location.assign(`${resolvedAuthBaseUrl}/logout`);
		});
	}, [
		fetchFn,
		resolvedAuthBaseUrl,
		trackError
	]);
	const validateResetPasswordToken = async (resetPasswordToken) => {
		try {
			return { success: !!(await client.query(ValidateResetPasswordToken_default, { resetPasswordToken }))?.data?.webPassword?.validateResetPasswordToken?.valid };
		} catch (err) {
			return {
				success: false,
				displayErrorMessage: "Error Validating Reset Password Token"
			};
		}
	};
	const resetPassword = async (token, password) => {
		try {
			if (!(await client.query("mutation resetPassword($resetPasswordToken: String!, $newPassword: String!) {\n  webPassword {\n    resetPassword(input: { resetPasswordToken: $resetPasswordToken, newPassword: $newPassword }) {\n      _empty\n    }\n  }\n}\n", {
				resetPasswordToken: token,
				newPassword: password
			})).error) return { success: true };
			return { success: false };
		} catch (err) {
			return { success: false };
		}
	};
	const verifyLogin = useCallback((credentials) => callVerifyLogin(resolvedAuthBaseUrl, fetchFn, trackError, credentials), [
		fetchFn,
		resolvedAuthBaseUrl,
		trackError
	]);
	const authentication = useMemo(() => parseAuthentication({
		data: queryResult.data,
		loading: queryResult.loading,
		error: queryResult.error
	}, trackError), [
		queryResult.loading,
		queryResult.data,
		queryResult.error,
		logout
	]);
	return /* @__PURE__ */ React.createElement(AuthContext.Provider, { value: {
		authentication,
		logout,
		refresh: queryResult.refresh,
		verifyLogin,
		validateResetPasswordToken,
		resetPassword
	} }, children);
};
async function callVerifyLogin(authBaseUrl, fetchFn, trackError, credentials) {
	let payload;
	if ("googleIdToken" in credentials) payload = { google_id_token: credentials.googleIdToken };
	else if ("facebookIdToken" in credentials) payload = { facebook_id_token: credentials.facebookIdToken };
	else payload = { human: {
		email: credentials.email,
		password: credentials.password
	} };
	let resp;
	try {
		resp = await fetchFn(`${authBaseUrl}/verify_login`, {
			method: "POST",
			credentials: "include",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json"
			},
			body: JSON.stringify(payload)
		});
	} catch (e) {
		trackError("login", e);
		return {
			success: false,
			displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE
		};
	}
	if (resp.ok) return { success: true };
	else {
		let displayErrorMessage = DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE;
		try {
			const json = await resp.json();
			if ("error" in json) displayErrorMessage = json["error"];
		} catch (e) {
			trackError("login error without error message", e);
		}
		return {
			success: false,
			displayErrorMessage
		};
	}
}
//#endregion
export { AuthContextProvider };

